import { useCallback, useRef } from "react";
import { App as AntdApp } from "antd";

/**
 * Ask before closing a create form that holds unsaved input.
 *
 * Escape, a press outside, the X and Cancel all close a drawer or wizard at
 * once, so one stray press threw away a filled-in form (platform test round
 * 3, "Lost work"). Route every way of closing through the function this
 * returns: an untouched form closes as before; a touched one asks first and
 * closes only on "Discard". A successful create calls `close` itself.
 */
/** The shell's own guard (`components/DiscardGuard`) has just asked and been
 *  told "Discard": the close that follows is not asked about again. */
let discardGrantedAt = 0;
export function grantDiscard(): void {
  discardGrantedAt = Date.now();
}

export function useDiscardGuard(close: () => void, isDirty: () => boolean): () => void {
  const { modal } = AntdApp.useApp();
  const asking = useRef(false);
  return useCallback(() => {
    if (!isDirty() || Date.now() - discardGrantedAt < 1000) {
      close();
      return;
    }
    if (asking.current) return; // one question at a time
    asking.current = true;
    modal.confirm({
      title: "Discard what you entered?",
      content: "Nothing has been created yet. Closing now loses what you filled in.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: () => {
        asking.current = false;
        close();
      },
      onCancel: () => {
        asking.current = false;
      },
    });
  }, [modal, close, isDirty]);
}
